// Client-side AES-256-GCM encryption using the browser's built-in Web Crypto API.
// The server never receives or stores a plaintext mnemonic — only this encrypted blob.
// Decryption requires the user's exact password; no server key is involved.

const PBKDF2_ITERATIONS = 100_000;

async function deriveKey(password, salt) {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw', enc.encode(password), { name: 'PBKDF2' }, false, ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: enc.encode(salt), iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

function toB64(buf) {
  return btoa(String.fromCharCode(...new Uint8Array(buf)));
}
function fromB64(s) {
  return Uint8Array.from(atob(s), c => c.charCodeAt(0));
}

// Returns an opaque string: "<iv_b64>:<ciphertext_b64>"
// Salt = email (unique per user, known during recovery)
export async function encryptMnemonic(mnemonic, password, email) {
  const key = await deriveKey(password, email);
  const iv  = crypto.getRandomValues(new Uint8Array(12));
  const ct  = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode(mnemonic)
  );
  return `${toB64(iv)}:${toB64(ct)}`;
}

// Throws if password or email is wrong — AES-GCM authentication tag fails
export async function decryptMnemonic(blob, password, email) {
  const [ivB64, ctB64] = blob.split(':');
  const key = await deriveKey(password, email);
  const plain = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: fromB64(ivB64) },
    key,
    fromB64(ctB64)
  );
  return new TextDecoder().decode(plain);
}
